import { c as _c } from "react/compiler-runtime";
import React from "react";
import NextLink from "next/link";
import { Icon } from "@/components/ui/Icon";
import { Tag } from "@/components/ui/Tag";
import type { IconName } from "@/types/icons";
import type { LinkData } from "@/types/link";
import { ThemeVariant } from "@/types";
import { cva } from "class-variance-authority";
import { Heading } from "./Heading";
const cardVariants = cva("group relative flex cursor-pointer flex-col justify-center gap-sm rounded-lg bg-purple p-md transition-colors outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2", {
  variants: {
    variant: {
      light: "text-white hover:bg-yellow focus-visible:bg-yellow focus-visible:text-black hover:text-black outline-yellow",
      dark: "text-white hover:bg-purple-dark focus-visible:bg-purple-dark outline-purple-dark",
      yellow: "text-black bg-yellow hover:bg-purple focus-visible:bg-purple focus-visible:text-white hover:text-white outline-purple"
    }
  },
  defaultVariants: {
    variant: "light"
  }
});
const iconVariants = cva("flex size-11 items-center justify-center rounded-full transition-colors", {
  variants: {
    variant: {
      light: "bg-yellow text-black group-hover:bg-purple group-focus-visible:bg-purple group-hover:text-white group-focus-visible:text-white",
      dark: "bg-yellow text-black",
      yellow: "bg-purple text-white group-hover:bg-yellow group-focus-visible:bg-yellow group-hover:text-black group-focus-visible:text-black"
    }
  },
  defaultVariants: {
    variant: "light"
  }
});

/**
 * Props for the LinkCard component
 */
export interface LinkCardProps {
  topline?: string;
  /** The card title */
  title: string;
  tags: {
    icon: IconName;
    label: string;
  }[];

  /** Optional link for navigation */
  link?: LinkData;
  variant?: ThemeVariant | "yellow";
  className?: string;
}

/**
 * LinkCard componment.
 * Implementation and styling to be added.
 */
export const LinkCard: React.FC<LinkCardProps> = t0 => {
  const $ = _c(30);
  const {
    topline,
    title,
    tags,
    link,
    variant,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== className || $[1] !== variant) {
    t2 = cardVariants({
      variant,
      className
    });
    $[0] = className;
    $[1] = variant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cardClassName = t2;
  let t3;
  if ($[3] !== topline) {
    t3 = topline && <p className="text-[16px] md:text-[18px]">{topline}</p>;
    $[3] = topline;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== title) {
    t4 = <Heading style="h5" tag="h3" className="mr-16">{title}</Heading>;
    $[5] = title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== tags) {
    t5 = tags.map(_temp);
    $[7] = tags;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <div className="flex flex-wrap gap-x-md gap-y-xs">{t5}</div>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== variant) {
    t7 = iconVariants({
      variant
    });
    $[11] = variant;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon name="arrow-corner-right-up" size={16} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = <div className="absolute top-sm right-sm"><div className={t7}>{t8}</div></div>;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t6 || $[19] !== t9) {
    t10 = <>{t3}{t4}{t6}{t9}</>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const content = t10;
  if (!link) {
    let t11;
    if ($[21] !== cardClassName || $[22] !== content) {
      t11 = <div className={cardClassName}>{content}</div>;
      $[21] = cardClassName;
      $[22] = content;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  let t11;
  if ($[24] !== cardClassName || $[25] !== content || $[26] !== link.href || $[27] !== link.rel || $[28] !== link.target) {
    t11 = <NextLink href={link.href} target={link.target} rel={link.rel} className={cardClassName}>{content}</NextLink>;
    $[24] = cardClassName;
    $[25] = content;
    $[26] = link.href;
    $[27] = link.rel;
    $[28] = link.target;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};
function _temp(tag, index) {
  return <Tag key={index} icon={tag.icon} label={tag.label} />;
}